import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
  InputAdornment,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { cardTileSx, cardAddTileSx, cardRemoveButtonSx, cardGridSx } from "../../../theme/cardTileTokens";

const numberInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
    "&:hover fieldset": { borderColor: "#163666" },
    "&.Mui-focused fieldset": { borderColor: "#163666", borderWidth: 2 },
    "&.Mui-error fieldset": { borderColor: "#ef4444 !important", borderWidth: "2px !important" },
  },
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
  "& input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
};

const descriptionFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
    "&:hover fieldset": { borderColor: "#163666" },
    "&.Mui-focused fieldset": { borderColor: "#163666", borderWidth: 2 },
    "&.Mui-error fieldset": { borderColor: "#ef4444 !important", borderWidth: "2px !important" },
  },
};

const SymptomBox = ({ checked, label, minutes, minutesLabel, onToggle, onMinutesChange, disabled, minutesError }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: { xs: "100%", md: 0 },
      p: 2,
      border: checked ? "2px solid #163666" : "1px solid #dfdfdf",
      borderRadius: 2,
      backgroundColor: checked ? "#eff6ff" : "#fafafa",
      cursor: disabled ? "default" : "pointer",
      transition: "all 0.15s ease",
      "&:hover": disabled ? {} : { borderColor: "#163666", backgroundColor: "#f5f9ff" },
    }}
    onClick={() => !disabled && onToggle()}
  >
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={(e) => { e.stopPropagation(); onToggle(); }}
          disabled={disabled}
          size="small"
          sx={{ color: "#163666", "&.Mui-checked": { color: "#163666" }, p: 0.75 }}
        />
      }
      label={
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
          {label}
        </Typography>
      }
      sx={{ mb: 0 }}
    />
    {checked && (
      <Box sx={{ mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
        <TextField
          fullWidth
          size="small"
          placeholder={`${minutesLabel} *`}
          type="number"
          value={minutes}
          onChange={(e) => onMinutesChange(e.target.value)}
          disabled={disabled}
          error={minutesError}
          sx={numberInputSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="caption" sx={{ color: "#6b7280" }}>{minutesLabel}</Typography>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    )}
  </Box>
);

export const TestSymptomsSection = ({
  formData,
  handleTestSymptomChange,
  handleOthersDescriptionChange,
  handleOthersMinutesChange,
  addOtherSymptom,
  removeOtherSymptom,
  isEditable,
  validationErrors,
  t,
}) => {
  const { bloating, diarrhoea, abdominalPain, others } = formData.testSymptoms;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Box sx={{ width: 3, height: 22, backgroundColor: "#163666", borderRadius: 2, flexShrink: 0 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a2e" }}>
          {t.testSymptoms}
        </Typography>
        <Typography variant="body2" sx={{ color: "#ef4444", fontWeight: 600 }}>*</Typography>
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: "#eff6ff",
          borderLeft: "4px solid #163666",
          borderRadius: "0 8px 8px 0",
        }}
      >
        <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.7 }}>
          {t.testSymptomsDescription}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        {/* Three main symptoms */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <SymptomBox
            checked={bloating.checked}
            label={t.bloating}
            minutes={bloating.minutes}
            minutesLabel={t.minutes}
            onToggle={() => handleTestSymptomChange("bloating", "checked", !bloating.checked)}
            onMinutesChange={(v) => handleTestSymptomChange("bloating", "minutes", v)}
            disabled={!isEditable}
            minutesError={!!validationErrors.bloatingMinutes}
          />
          <SymptomBox
            checked={diarrhoea.checked}
            label={t.diarrhoea}
            minutes={diarrhoea.minutes}
            minutesLabel={t.minutes}
            onToggle={() => handleTestSymptomChange("diarrhoea", "checked", !diarrhoea.checked)}
            onMinutesChange={(v) => handleTestSymptomChange("diarrhoea", "minutes", v)}
            disabled={!isEditable}
            minutesError={!!validationErrors.diarrhoeaMinutes}
          />
          <SymptomBox
            checked={abdominalPain.checked}
            label={t.abdominalPain}
            minutes={abdominalPain.minutes}
            minutesLabel={t.minutes}
            onToggle={() => handleTestSymptomChange("abdominalPain", "checked", !abdominalPain.checked)}
            onMinutesChange={(v) => handleTestSymptomChange("abdominalPain", "minutes", v)}
            disabled={!isEditable}
            minutesError={!!validationErrors.abdominalPainMinutes}
          />
        </Box>

        {/* Others */}
        <Box
          sx={{
            p: 2,
            border: others.checked ? "2px solid #163666" : "1px solid #dfdfdf",
            borderRadius: 2,
            backgroundColor: others.checked ? "#eff6ff" : "#fafafa",
            cursor: isEditable ? "pointer" : "default",
            transition: "all 0.15s ease",
            "&:hover": !isEditable ? {} : { borderColor: "#163666", backgroundColor: "#f5f9ff" },
          }}
          onClick={() =>
            isEditable && handleTestSymptomChange("others", "checked", !others.checked)
          }
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={others.checked}
                onChange={(e) => { e.stopPropagation(); handleTestSymptomChange("others", "checked", e.target.checked); }}
                disabled={!isEditable}
                size="small"
                sx={{ color: "#163666", "&.Mui-checked": { color: "#163666" }, p: 0.75 }}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>{t.others}</Typography>
                <Typography variant="caption" sx={{ color: "#6b7280" }}>
                  {t.maximumSymptoms}
                </Typography>
              </Box>
            }
          />

          {others.checked && (
            <Box sx={{ mt: 2 }} onClick={(e) => e.stopPropagation()}>
              <Box sx={cardGridSx}>
                {others.symptoms.map((symptom, index) => (
                  <Box
                    key={index}
                    sx={{
                      ...cardTileSx,
                      height: "auto",
                      minHeight: 176,
                      justifyContent: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    {isEditable && others.symptoms.length > 1 && (
                      <IconButton
                        size="small"
                        aria-label={t.removeSymptom}
                        onClick={() => removeOtherSymptom(index)}
                        sx={cardRemoveButtonSx}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={`${t.describeOtherSymptoms} *`}
                      value={symptom.description}
                      onChange={(e) => handleOthersDescriptionChange(index, e.target.value)}
                      disabled={!isEditable}
                      error={symptom.wordCount > 3 || !!validationErrors[`otherSymptomDescription_${index}`]}
                      helperText={
                        symptom.wordCount > 3
                          ? `${t.toomanywords} (${symptom.wordCount}/3). ${t.keepbrief}`
                          : `${symptom.wordCount || 0}/3 ${t.wordsUsed}`
                      }
                      sx={descriptionFieldSx}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={`${t.minutes} *`}
                      type="number"
                      value={symptom.minutes}
                      onChange={(e) => handleOthersMinutesChange(index, e.target.value)}
                      disabled={!isEditable}
                      error={!!validationErrors[`otherSymptomMinutes_${index}`]}
                      sx={numberInputSx}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="caption" sx={{ color: "#6b7280" }}>
                              {t.minutes}
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                ))}

                {isEditable && others.symptoms.length < 3 && (
                  <Box
                    onClick={addOtherSymptom}
                    sx={{ ...cardAddTileSx, minHeight: 176, height: "auto" }}
                  >
                    <AddIcon />
                  </Box>
                )}
              </Box>

              {others.symptoms.length >= 3 && (
                <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#6b7280" }}>
                  {t.maximumSymptomsReached}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 4, borderColor: "#dfdfdf" }} />
    </>
  );
};
